import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Checkbox,
  DateField,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@structural-climbing/ui'
import { ageFrom } from './ageFrom'

/** What the gym has on record for a member it added, to correct. */
export interface DeskMemberDetails {
  userId: string
  firstName: string | null
  lastName: string | null
  dateOfBirth: string | null
  gender: string | null
  /** The member's own for an adult, the parent's for a minor. */
  phone: string | null
  email: string | null
  guardianFirstName: string | null
  guardianLastName: string | null
}

interface Props {
  /**
   * Set to correct a member the gym added earlier: the form opens filled in
   * and saves over them. Left out, the form adds someone new.
   */
  existing?: DeskMemberDetails
  /** The member's id, so the page can go straight on to giving a new one a plan. */
  onAdded: (member: { userId: string; name: string; email: string }) => void
  onClose: () => void
}

type Field =
  | 'firstName'
  | 'lastName'
  | 'dateOfBirth'
  | 'gender'
  | 'guardianFirstName'
  | 'guardianLastName'
  | 'phone'
  | 'email'

const GENDERS = ['male', 'female', 'prefer_not_to_say'] as const

/**
 * The gym adding someone who cannot ask to join from their own phone: no email,
 * a grandparent, a child who came with a relative.
 *
 * Someone under 18 needs a parent or guardian named, and the phone or email is
 * then that adult's. The tick only shows what the date of birth says; the
 * Worker decides for itself.
 *
 * The same form corrects such a member later (`existing`). Every field can be
 * changed, the email and the parent included, because nobody is behind that
 * profile to keep it current themselves.
 */
export function AddMemberModal({ existing, onAdded, onClose }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [firstName, setFirstName] = useState(existing?.firstName ?? '')
  const [lastName, setLastName] = useState(existing?.lastName ?? '')
  const [dateOfBirth, setDateOfBirth] = useState(existing?.dateOfBirth ?? '')
  const [gender, setGender] = useState<string | null>(existing?.gender ?? null)
  const [guardianFirstName, setGuardianFirstName] = useState(existing?.guardianFirstName ?? '')
  const [guardianLastName, setGuardianLastName] = useState(existing?.guardianLastName ?? '')
  const [phone, setPhone] = useState(existing?.phone ?? '')
  const [email, setEmail] = useState(existing?.email ?? '')
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const isMinor = /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth) && ageFrom(dateOfBirth) < 18

  const genderLabels: Record<(typeof GENDERS)[number], string> = {
    male: t('members.genderMale'),
    female: t('members.genderFemale'),
    prefer_not_to_say: t('members.genderPreferNotToSay'),
  }

  async function handleSave() {
    const required = t('members.editDetailsRequired')
    const errors: Partial<Record<Field, string>> = {}
    if (!firstName.trim()) errors.firstName = required
    if (!lastName.trim()) errors.lastName = required
    if (!dateOfBirth) errors.dateOfBirth = required
    if (isMinor) {
      if (!gender) errors.gender = t('members.addMemberGenderRequired')
      if (!guardianFirstName.trim()) errors.guardianFirstName = required
      if (!guardianLastName.trim()) errors.guardianLastName = required
    }
    if (!phone.trim() && !email.trim()) {
      errors.phone = t('members.addMemberContactRequired')
      errors.email = t('members.addMemberContactRequired')
    }

    setFieldErrors(errors)
    setError(null)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      const token = await getAccessTokenSilently()
      const url = existing
        ? `${apiUrl}/members/${encodeURIComponent(existing.userId)}`
        : `${apiUrl}/members`
      const res = await fetch(url, {
        method: existing ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth,
          ...(gender ? { gender } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(isMinor
            ? {
                guardianFirstName: guardianFirstName.trim(),
                guardianLastName: guardianLastName.trim(),
              }
            : {}),
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        code?: string
        user_id?: string
        is_minor?: boolean
      }
      const savedId = existing?.userId ?? data.user_id
      if (!res.ok || !savedId) {
        if (data.code === 'PHONE_INVALID')
          setFieldErrors({ phone: t('members.editDetailsPhoneInvalid') })
        else if (data.code === 'EMAIL_INVALID')
          setFieldErrors({ email: t('members.addMemberEmailInvalid') })
        else if (data.code === 'MEMBER_EXISTS')
          setFieldErrors({ email: t('members.addMemberExists') })
        else if (data.code === 'EMAIL_IN_USE')
          setFieldErrors({ email: t('members.addMemberEmailInUse') })
        else if (data.code === 'CONTACT_REQUIRED')
          setFieldErrors({
            phone: t('members.addMemberContactRequired'),
            email: t('members.addMemberContactRequired'),
          })
        else if (data.code === 'GUARDIAN_REQUIRED')
          setFieldErrors({ guardianFirstName: required, guardianLastName: required })
        else if (data.code === 'GENDER_REQUIRED')
          setFieldErrors({ gender: t('members.addMemberGenderRequired') })
        else setError(t('common.errorDefault'))
        return
      }
      onAdded({
        userId: savedId,
        name: `${firstName.trim()} ${lastName.trim()}`,
        // A minor's email is the parent's, and is not on the member's own row.
        email: data.is_minor ? '' : email.trim().toLowerCase(),
      })
    } catch {
      setError(t('common.networkError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      scrollBehavior="inside"
      isDismissable={!saving}
      onOpenChange={(open) => {
        if (!open && !saving) onClose()
      }}
    >
      <ModalContent>
        <form
          className="flex min-h-0 flex-col"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void handleSave()
          }}
        >
          <ModalHeader>
            {existing ? t('members.editDetails') : t('members.addMemberTitle')}
          </ModalHeader>
          <ModalBody>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('members.detailsFirstName')}
                value={firstName}
                onValueChange={setFirstName}
                maxLength={100}
                isInvalid={!!fieldErrors.firstName}
                errorMessage={fieldErrors.firstName}
              />
              <Input
                label={t('members.detailsLastName')}
                value={lastName}
                onValueChange={setLastName}
                maxLength={100}
                isInvalid={!!fieldErrors.lastName}
                errorMessage={fieldErrors.lastName}
              />
              <DateField
                label={t('members.detailsDateOfBirth')}
                value={dateOfBirth}
                onValueChange={setDateOfBirth}
                isInvalid={!!fieldErrors.dateOfBirth}
                errorMessage={fieldErrors.dateOfBirth}
              />
              <Select
                label={t('members.detailsGender')}
                selectedKeys={gender ? new Set([gender]) : new Set<string>()}
                onSelectionChange={(keys) => {
                  const key = [...keys][0] as string | undefined
                  setGender(key ?? null)
                }}
                isInvalid={!!fieldErrors.gender}
                errorMessage={fieldErrors.gender}
              >
                {GENDERS.map((key) => (
                  <SelectItem key={key}>{genderLabels[key]}</SelectItem>
                ))}
              </Select>
            </div>

            <Checkbox isSelected={isMinor} isReadOnly size="sm">
              {t('members.addMemberUnder18')}
            </Checkbox>

            {isMinor && (
              <div role="group" aria-label={t('members.addMemberGuardianTitle')}>
                <p className="mb-2 text-sm font-medium">{t('members.addMemberGuardianTitle')}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label={t('members.detailsFirstName')}
                    value={guardianFirstName}
                    onValueChange={setGuardianFirstName}
                    maxLength={100}
                    isInvalid={!!fieldErrors.guardianFirstName}
                    errorMessage={fieldErrors.guardianFirstName}
                  />
                  <Input
                    label={t('members.detailsLastName')}
                    value={guardianLastName}
                    onValueChange={setGuardianLastName}
                    maxLength={100}
                    isInvalid={!!fieldErrors.guardianLastName}
                    errorMessage={fieldErrors.guardianLastName}
                  />
                </div>
              </div>
            )}

            <div role="group" aria-label={t('members.addMemberContactTitle')}>
              <p className="text-sm font-medium">{t('members.addMemberContactTitle')}</p>
              <p className="mb-2 text-xs text-foreground/60">
                {isMinor
                  ? t('members.addMemberContactHintMinor')
                  : t('members.addMemberContactHint')}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  type="tel"
                  label={t('members.detailsPhone')}
                  value={phone}
                  onValueChange={setPhone}
                  maxLength={20}
                  isInvalid={!!fieldErrors.phone}
                  errorMessage={fieldErrors.phone}
                />
                <Input
                  type="email"
                  label={t('common.email')}
                  value={email}
                  onValueChange={setEmail}
                  maxLength={254}
                  isInvalid={!!fieldErrors.email}
                  errorMessage={fieldErrors.email}
                />
              </div>
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" isDisabled={saving} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button color="primary" type="submit" isLoading={saving}>
              {existing ? t('common.save') : t('members.addMember')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
